import { useEffect, useRef } from 'react';

import { beginVisitorScope, sendBrochureBeacon } from '../utils/brochureTracking';

// Brackets a brochure visit in our first-party engagement store: a 'start'
// beacon once the brochure creds arrive opens the visit row, an 'end' beacon
// closes it. Per-detail 'view' beacons (useDetailViewTracking) hang off the
// same visitor uuid in between. Source-agnostic — `tracking` carries the
// endpoint + token + identity for whichever brochure is mounted (the resident
// PIB brochure or the TourGuide AI lead brochure).
//
// The visit is scoped to the brochure's SUBJECT, not to this component's
// lifetime. A lead who has converted is redirected from the lead brochure to
// the resident's own (BrochurePage handles `data.redirect`), and that is a
// history.replace: no pagehide fires, the component may or may not remount, and
// the creds swap underneath. Bracketing on mount/pagehide alone left the lead
// visit permanently open and never opened the resident one, with both sharing a
// visitor uuid. So the effect keys on the identity: when it changes, the old
// visit is closed with the creds that opened it, the tab takes a fresh visitor
// uuid, and the new visit opens. That "which subject was this tab last on?"
// guard lives in sessionStorage (beginVisitorScope), not in a ref here: the
// uuid it guards has tab lifetime, so a mount-lifetime ref let a second
// brochure in the same tab reuse the first visit's uuid (#1129).
//
// pagehide (not unload) covers the real navigation away / tab close, and does
// not fire on tab-switch or minimize; sendBeacon survives teardown. Every leg
// is once-guarded per visit so a re-render or a pagehide followed by unmount
// cannot double up.
const useBrochureVisitBeacon = ({ tracking }) => {
  const hasTracking = !!(tracking && tracking.endpoint && tracking.token && tracking.identity);

  // Identity of the visit currently open. Two brochures for two different
  // subjects are two visits even when the same component renders both.
  const visitKey = hasTracking
    ? `${tracking.endpoint}|${JSON.stringify(tracking.identity)}`
    : null;

  // The pagehide listener is registered once, so read live creds via a ref.
  const trackingRef = useRef(null);
  trackingRef.current = hasTracking ? tracking : null;
  const endedRef = useRef(false);

  useEffect(() => {
    if (!visitKey) return undefined;

    // A different subject in the same tab: the previous visit has already been
    // closed (by this effect's cleanup, or by an earlier mount's), so the uuid
    // that identified it must not carry into the next one.
    beginVisitorScope(visitKey);

    // Capture the creds that open this visit: the cleanup must close the visit
    // it started, not whichever brochure has since been fetched.
    const visitTracking = trackingRef.current;
    endedRef.current = false;
    sendBrochureBeacon('start', { tracking: visitTracking });

    return () => {
      if (endedRef.current) return;
      endedRef.current = true;
      sendBrochureBeacon('end', { tracking: visitTracking });
    };
  }, [visitKey]);

  useEffect(() => {
    const handlePageHide = () => {
      const liveTracking = trackingRef.current;
      if (endedRef.current || !liveTracking) return;
      endedRef.current = true;
      sendBrochureBeacon('end', { tracking: liveTracking });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);
};

export default useBrochureVisitBeacon;
